import React, { useState } from 'react';
import {
  Table, Card, Button, Space, Typography, Form, Input, Select,
  Tag, Drawer, InputNumber, message, Modal, Radio, Cascader, Tooltip
} from 'antd';
import { PlusOutlined, SearchOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

// ══════════════════════════════════════════════
//  Mock 数据
// ══════════════════════════════════════════════

// 合作企业（分润企业 = 设备企业）
// TODO: 联调时替换为接口数据
const MOCK_COOP_ENTERPRISES = [
  { value: 'test-ent-001', label: '杭州智联科技有限公司 (test-ent-001)' },
  { value: 'test-ent-002', label: '成都云视科技有限公司 (test-ent-002)' },
  { value: 'test-ent-003', label: '广州云联物联网有限公司 (test-ent-003)' },
];

// App Cascader 选项：第一级 App 名称，第二级 系统-区域
const MOCK_APP_CASCADE = [
  { value: 'test-app-001', label: '智能家居控制 App (test-app-001)', children: [
    { value: 'ios-cn', label: 'iOS-国内' },
    { value: 'ios-global', label: 'iOS-全球' },
    { value: 'android-cn', label: 'Android-国内' },
    { value: 'android-global', label: 'Android-全球' },
  ]},
  { value: 'test-app-002', label: '云视监控 App (test-app-002)', children: [
    { value: 'ios-cn', label: 'iOS-国内' },
    { value: 'android-cn', label: 'Android-国内' },
    { value: 'android-global', label: 'Android-全球' },
  ]},
];

// 数仓返回的全球国家列表
// TODO: 联调时替换为接口数据
const MOCK_COUNTRIES = [
  '中国', '美国', '日本', '韩国', '德国', '英国', '法国', '澳大利亚', '加拿大', '新加坡',
];

// TODO: 联调时替换为接口数据
const MOCK_RULES = [
  { key: '1', entId: 'test-ent-001', entName: '杭州智联科技有限公司', mode: 'manual', appIds: [['test-app-001', 'ios-cn'], ['test-app-001', 'ios-global']], appLabels: '智能家居控制 iOS-国内, iOS-全球', baseRatio: 80, customRatios: [{ country: '美国', ratio: 90 }], status: 'active', updateTime: '2026-07-28 14:30:00' },
  { key: '2', entId: 'test-ent-002', entName: '成都云视科技有限公司', mode: 'manual', appIds: [['test-app-002', 'android-cn'], ['test-app-002', 'android-global']], appLabels: '云视监控 Android-国内, Android-全球', baseRatio: 60, customRatios: [], status: 'active', updateTime: '2026-07-27 10:15:00' },
  { key: '3', entId: 'test-ent-003', entName: '广州云联物联网有限公司', mode: 'auto', appIds: [], appLabels: '全部 App', baseRatio: 0, customRatios: [{ country: '日本', ratio: 70 }], status: 'stopped', updateTime: '2026-07-25 09:00:00' },
  { key: '4', entId: 'test-ent-001', entName: '杭州智联科技有限公司', mode: 'manual', appIds: [['test-app-001', 'android-cn']], appLabels: '智能家居控制 Android-国内', baseRatio: 100, customRatios: [], status: 'active', updateTime: '2026-07-30 16:45:00' },
];

const MODE_MAP = { manual: '手动配置', auto: '自动配置' };

// 将 App Cascader 值转为展示文本
const formatAppLabels = (appValues) => {
  if (!appValues || appValues.length === 0) return '全部 App';
  return appValues.map(([appId, ver]) => {
    const app = MOCK_APP_CASCADE.find((a) => a.value === appId);
    const verLabel = app?.children?.find((c) => c.value === ver)?.label || ver;
    return app ? `${app.label.split(' ')[0]} ${verLabel}` : `${appId}-${ver}`;
  }).join(', ');
};

// 比例展示：统一比例 + 自定义国家 Tag
const renderRatio = (record) => (
  <Space size={4} wrap>
    <Text strong style={{ color: '#1677ff' }}>{record.baseRatio}%</Text>
    {record.customRatios && record.customRatios.length > 0 && (
      record.customRatios.length <= 2
        ? record.customRatios.map((c, i) => (
            <Tooltip key={i} title={`${c.country} 自定义比例`}>
              <Tag color="orange" style={{ border: 'none' }}>{c.country} {c.ratio}%</Tag>
            </Tooltip>
          ))
        : <Tooltip title={record.customRatios.map((c) => `${c.country} ${c.ratio}%`).join('；')}>
            <Tag color="orange" style={{ border: 'none' }}>自定义{record.customRatios.length}国</Tag>
          </Tooltip>
    )}
  </Space>
);

const Config = () => {
  const [dataSource, setDataSource] = useState(MOCK_RULES);
  const [filterEnt, setFilterEnt] = useState('');
  const [filterApp, setFilterApp] = useState('');
  const [filterStatus, setFilterStatus] = useState(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const modeWatch = Form.useWatch('mode', form);

  const filteredData = dataSource.filter((r) => {
    if (filterEnt && !(r.entName + r.entId).toLowerCase().includes(filterEnt.toLowerCase())) return false;
    if (filterApp && !r.appLabels.toLowerCase().includes(filterApp.toLowerCase())) return false;
    if (filterStatus && r.status !== filterStatus) return false;
    return true;
  });

  // 已配置的企业列表（用于不可重复校验）
  const usedEntIds = editingRecord
    ? dataSource.filter((r) => r.key !== editingRecord.key).map((r) => r.entId)
    : dataSource.map((r) => r.entId);

  const openDrawer = (record) => {
    if (record) {
      setEditingRecord(record);
      form.setFieldsValue({
        entId: record.entId,
        mode: record.mode,
        appIds: record.mode === 'manual' ? record.appIds : undefined,
        baseRatio: record.baseRatio,
        customRatios: record.customRatios && record.customRatios.length > 0 ? record.customRatios : [],
      });
    } else {
      setEditingRecord(null);
      form.resetFields();
      form.setFieldsValue({ mode: 'manual', baseRatio: 0, customRatios: [] });
    }
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const ent = MOCK_COOP_ENTERPRISES.find((e) => e.value === values.entId);
      const customRatios = (values.customRatios || []).filter((c) => c.country && c.ratio != null);

      if (editingRecord) {
        setDataSource((prev) =>
          prev.map((r) =>
            r.key === editingRecord.key
              ? {
                  ...r,
                  entId: values.entId,
                  entName: ent?.label.split(' ')[0] || r.entName,
                  mode: values.mode,
                  appIds: values.mode === 'manual' ? values.appIds : [],
                  appLabels: values.mode === 'manual' ? formatAppLabels(values.appIds) : '全部 App',
                  baseRatio: values.baseRatio,
                  customRatios,
                  updateTime: new Date().toISOString().replace('T', ' ').slice(0, 19),
                }
              : r,
          ),
        );
        message.success('分润规则编辑成功，下一结算周期生效');
      } else {
        const newRecord = {
          key: Date.now().toString(),
          entId: values.entId,
          entName: ent?.label.split(' ')[0] || '',
          mode: values.mode,
          appIds: values.mode === 'manual' ? values.appIds : [],
          appLabels: values.mode === 'manual' ? formatAppLabels(values.appIds) : '全部 App',
          baseRatio: values.baseRatio,
          customRatios,
          status: 'active',
          updateTime: new Date().toISOString().replace('T', ' ').slice(0, 19),
        };
        setDataSource((prev) => [newRecord, ...prev]);
        message.success('分润规则创建成功，下一结算周期生效');
      }
      setDrawerOpen(false);
    } catch {
      // validation failed
    }
  };

  const toggleStatus = (record) => {
    const isStopping = record.status === 'active';
    Modal.confirm({
      title: isStopping ? '停用分润规则' : '启用分润规则',
      content: isStopping
        ? '停用后该规则将不再参与后续分润计算，已生成的账单不受影响。确认停用？'
        : '启用后该规则将在下一结算周期重新参与分润计算。确认启用？',
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        setDataSource((prev) =>
          prev.map((r) =>
            r.key === record.key
              ? { ...r, status: isStopping ? 'stopped' : 'active', updateTime: new Date().toISOString().replace('T', ' ').slice(0, 19) }
              : r,
          ),
        );
        message.success(isStopping ? '规则已停用' : '规则已启用');
      },
    });
  };

  const deleteRule = (record) => {
    Modal.confirm({
      title: '删除分润规则',
      content: '删除后不可恢复，确认删除？',
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        setDataSource((prev) => prev.filter((r) => r.key !== record.key));
        message.success('规则已删除');
      },
    });
  };

  const columns = [
    { title: '分润企业', dataIndex: 'entName', width: 150, render: (text, r) => <div><Text strong>{text}</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>{r.entId}</Text></div> },
    {
      title: '分润 App', dataIndex: 'appLabels', width: 220,
      render: (text, r) => (
        r.mode === 'auto'
          ? <Tag color="purple" style={{ border: 'none' }}>全部 App</Tag>
          : <Space size={4} wrap>{text.split(', ').map((s, i) => <Tag key={i} color="blue" style={{ border: 'none' }}>{s}</Tag>)}</Space>
      ),
    },
    { title: '配置模式', dataIndex: 'mode', width: 90, render: (v) => <Tag color={v === 'manual' ? 'blue' : 'purple'} style={{ border: 'none' }}>{MODE_MAP[v]}</Tag> },
    { title: '分润比例', key: 'ratio', width: 180, render: (_, r) => renderRatio(r) },
    { title: '生效状态', dataIndex: 'status', width: 90, render: (v) => <Tag color={v === 'active' ? 'green' : 'default'} style={{ border: 'none' }}>{v === 'active' ? '生效中' : '已停用'}</Tag> },
    { title: '操作时间', dataIndex: 'updateTime', width: 160, render: (v) => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> },
    {
      title: '操作', key: 'action', width: 200, fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => openDrawer(r)}>编辑</Button>
          <Button type="link" size="small" onClick={() => toggleStatus(r)}>
            {r.status === 'active' ? '停用' : '启用'}
          </Button>
          <Button type="link" size="small" danger onClick={() => deleteRule(r)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>分润配置</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer(null)}>创建规则</Button>
      </div>

      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form layout="inline">
          <Form.Item label="分润企业" style={{ marginBottom: 0 }}>
            <Input placeholder="搜索企业名称/ID" prefix={<SearchOutlined />} style={{ width: 180 }} value={filterEnt} onChange={(e) => setFilterEnt(e.target.value)} allowClear />
          </Form.Item>
          <Form.Item label="分润 App" style={{ marginBottom: 0 }}>
            <Input placeholder="搜索 App 名称" prefix={<SearchOutlined />} style={{ width: 170 }} value={filterApp} onChange={(e) => setFilterApp(e.target.value)} allowClear />
          </Form.Item>
          <Form.Item label="状态" style={{ marginBottom: 0 }}>
            <Select placeholder="全部" allowClear style={{ width: 110 }} value={filterStatus} onChange={setFilterStatus}>
              <Option value="active">生效中</Option>
              <Option value="stopped">已停用</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button icon={<ReloadOutlined />} onClick={() => { setFilterEnt(''); setFilterApp(''); setFilterStatus(undefined); }}>重置</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="key"
          bordered={false}
          scroll={{ x: 1350 }}
          locale={{ emptyText: '暂无分润规则，点击右上角「创建规则」开始配置' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
        />
      </Card>

      {/* 创建/编辑 Drawer */}
      <Drawer
        title={editingRecord ? '编辑分润规则' : '创建分润规则'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={560}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button style={{ marginRight: 8 }} onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" onClick={handleSubmit}>保存</Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="entId"
            label="分润企业（设备企业）"
            rules={[{ required: true, message: '请选择分润企业' }]}
          >
            <Select placeholder="从合作企业中选择" showSearch optionFilterProp="children" disabled={!!editingRecord}>
              {MOCK_COOP_ENTERPRISES.map((e) => (
                <Option key={e.value} value={e.value} disabled={usedEntIds.includes(e.value)}>
                  {e.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="mode" label="配置模式" rules={[{ required: true, message: '请选择配置模式' }]}>
            <Radio.Group disabled={!!editingRecord}>
              <Radio.Button value="manual">手动配置</Radio.Button>
              <Radio.Button value="auto">自动配置</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {modeWatch === 'manual' ? (
            <Form.Item name="appIds" label="分润 App" rules={[{ required: true, message: '请选择分润 App' }]}>
              <Cascader options={MOCK_APP_CASCADE} placeholder="选择 App → 系统-区域" multiple maxTagCount="responsive" style={{ width: '100%' }} />
            </Form.Item>
          ) : (
            <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8, marginBottom: 24 }}>
              <Text type="secondary">自动配置模式：对名下所有 App 的全部版本生效，无需选择具体 App。</Text>
            </div>
          )}

          <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8, marginBottom: 24 }}>
            <Form.Item
              name="baseRatio"
              label={
                <Space size={4}>
                  统一分润比例
                  <Tooltip title="统一配置，对所有国家生效；可设为 0%（该国不分润）">
                    <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>ⓘ</Text>
                  </Tooltip>
                </Space>
              }
              rules={[
                { required: true, message: '请输入统一分润比例' },
                { type: 'number', min: 0, max: 100, message: '分润比例需在 0%〜100% 之间' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="对所有国家生效，可设为 0%（该国不分润）"
                min={0}
                max={100}
                precision={0}
                addonAfter="%"
              />
            </Form.Item>

            <div style={{ marginBottom: 8 }}>
              <Text strong>自定义国家比例</Text>
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>自定义优先于统一比例，可选</Text>
            </div>
            <Form.List name="customRatios">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                      <Form.Item {...restField} name={[name, 'country']} style={{ marginBottom: 0 }} rules={[{ required: true, message: '请选择国家' }]}>
                        <Select placeholder="国家" allowClear showSearch style={{ width: 160 }}>
                          {MOCK_COUNTRIES.map((c) => <Option key={c} value={c}>{c}</Option>)}
                        </Select>
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'ratio']} style={{ marginBottom: 0 }} rules={[
                        { required: true, message: '请输入比例' },
                        { type: 'number', min: 0, max: 100, message: '0~100' },
                      ]}>
                        <InputNumber placeholder="比例%" min={0} max={100} precision={0} addonAfter="%" style={{ width: 120 }} />
                      </Form.Item>
                      <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} danger />
                    </Space>
                  ))}
                  <Button type="dashed" block onClick={() => add()} style={{ marginTop: 4 }}>+ 添加自定义国家比例</Button>
                </>
              )}
            </Form.List>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default Config;
